import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import { Button } from '../../styles/components';
import { gsap } from 'gsap';



// Styled components for the login page
const LoginContainer = styled.div`
  background: black;
  flex-direction: row;
  display: flex;
  min-height: 100vh;
  width: 100%;
  position: relative;
  font-family: 'Inter', 'Roboto', sans-serif;
  background-color: #121829;
  color: #fff;
`;

const HomeButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  padding: 8px 16px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: translateY(-2px);
  }

  svg {
    margin-right: 8px;
  }
`;

const ImageSection = styled.div`
  flex: 1.5;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at center, rgba(25, 33, 55, 0.8) 0%, rgba(18, 24, 41, 1) 70%);
  
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(18, 24, 41, 0.2);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroImage = styled.img`
  max-width: 70%;
  height: auto;
  position: absolute;
  top: 10%;
  left: 10%;
  z-index: 2;
`;

const OrbitElement = styled.div`
  position: absolute;
  z-index: 3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
`;

const BlurredBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  
  svg {
    width: 80%;
    height: 100%;
  }
`;

const FormSection = styled.div`
z-index: 2;
  flex: 1.2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 2rem;
  background: transparent;
`;

const FormContainer = styled.div`
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 10 auto;
  background: #F0F0F0;
  border-radius: 16px;
  padding: 2rem ;
  box-shadow: 0 0 10px #ff0000, 0 0 20px #00ff00, 0 0 30px #0000ff;
  max-width: 450px;
  width: 100%;
  color: #000000;
`;

const WelcomeHeader = styled.div`
  margin-bottom: 2rem;
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const WelcomeText = styled.p`
  font-size: 1rem;
  color: #666666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const LoginForm = styled.form`
  width: 100%;
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  background-color: #FFFFFF;
  border: 1px solid #ccc;
  color: #000000;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  span {
    position: absolute;
    left: 16px;
  }
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #000000;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  color: #000000;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  
  &:focus {
    outline: none;
    border-color: #4285F4;
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
  color: #000000;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
`;

const StyledLoginButton = styled(Button)`
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  background-color: #000000;
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background-color: #333333;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const AccountText = styled.p`
  text-align: center;
  color: #666666;
  
  a {
    color: #007BFF;
    text-decoration: none;
    font-weight: 500;
    margin-left: 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  
  // Refs for animation
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const orbitElementsRef = useRef([]);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Animation effect
  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Image animation
      const imageTimeline = gsap.timeline();
      
      imageTimeline
        .fromTo(
          ".hero-image",
          { scale: 0.9, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1, ease: "power2.out" }
        )
        .fromTo(
          ".orbit-element",
          { scale: 0, opacity: 0, rotation: -45 },
          {
            scale: 1,
            opacity: 1,
            rotation: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: "back.out(1.7)",
          },
          "-=0.5"
        );

      // Continuous floating animation
      gsap.to(".hero-image", {
        y: 15,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });

      // Continuous rotation for orbit elements
      gsap.to(".orbit-element", {
        rotation: 360,
        duration: 15,
        ease: "none",
        repeat: -1,
        transformOrigin: "center center",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    
    // Determine user role based on email domain
    let role = 'student'; // Default role
    
    if (email.includes('@student')) {
      role = 'student';
    } else if (email.includes('@prostudent')) {
      role = 'proStudent';
    } else if (email.includes('@company')) {
      role = 'company';
    } else if (email.includes('@scadoffice')) {
      role = 'scadOffice';
    } else if (email.includes('@facultymember')) {
      role = 'faculty';
    }
    
    // Call the login function with the determined role
    login(email, role);
  };

  const handleGoogleSignIn = () => {
  window.location.href = 'https://apps.guc.edu.eg';
  };
  

  return (
    <LoginContainer>
      <HomeButton to="/">
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
        Home
      </HomeButton>
      
      <ImageSection ref={sectionRef}>
        <BlurredBackground>
          <svg
            style={{ filter: "blur(64px)" }}
            width="100%"
            height="100%"
            viewBox="0 0 444 536"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M225.919 112.719C343.98 64.6648 389.388 -70.487 437.442 47.574C485.496 165.635 253.266 481.381 135.205 529.435C17.1445 577.488 57.9596 339.654 9.9057 221.593C-38.1482 103.532 107.858 160.773 225.919 112.719Z"
              fill="url(#c)"
            />
            <defs>
              <linearGradient
                id="c"
                x1="200"
                y1="550.792"
                x2="-39.945"
                y2="150"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="0100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>
        </BlurredBackground>
        
        <HeroImageContainer ref={imageRef}>
          <HeroImage 
            className="hero-image"
            src="/assets/images/login/login-bg.png" 
            alt="Student with graduation cap"
          />
          
          {/* Orbit Elements */}
          <OrbitElement 
            className="orbit-element"
            ref={el => orbitElementsRef.current[0] = el}
            style={{ 
              top: '10%', 
              left: '20%', 
              width: '60px',
              height: '60px',
              background: 'linear-gradient(to right, #22d3ee, #3b82f6)'
            }}
          >
            <svg
              className="w-8 h-8 text-white"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                fill="white"
              />
              <path d="M8 16L12 12L16 16" fill="white" />
              <path
                d="M12 8V16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </OrbitElement>
          
          <OrbitElement 
            className="orbit-element"
            ref={el => orbitElementsRef.current[1] = el}
            style={{ 
              bottom: '20%', 
              left: '10%', 
              width: '60px',
              height: '60px',
              background: 'linear-gradient(to right, #a855f7, #ec4899)'
            }}
          >
            <svg
              className="w-8 h-8 text-white"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                fill="white"
              />
            </svg>
          </OrbitElement>
          
          <OrbitElement 
            className="orbit-element"
            ref={el => orbitElementsRef.current[2] = el}
            style={{ 
              top: '30%', 
              right: '10%', 
              width: '60px',
              height: '60px',
              background: 'linear-gradient(to right, #f59e0b, #f97316)'
            }}
          >
            <svg
              className="w-8 h-8 text-white"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z"
                fill="white"
              />
            </svg>
          </OrbitElement>
          
          <OrbitElement 
            className="orbit-element"
            ref={el => orbitElementsRef.current[3] = el}
            style={{ 
              bottom: '15%', 
              right: '20%', 
              width: '60px',
              height: '60px',
              background: 'linear-gradient(to right, #10b981, #059669)'
            }}
          >
            <svg
              className="w-8 h-8 text-white"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                fill="white"
              />
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                fill="white"
              />
            </svg>
          </OrbitElement>
        </HeroImageContainer>
      </ImageSection>
      
      <FormSection>
        <FormContainer>
          
          <WelcomeHeader>
            
            <WelcomeTitle>Welcome back 👋</WelcomeTitle>
            <WelcomeText>
              Sign in to connect with real opportunities, real experience, real growth.
            </WelcomeText>
          </WelcomeHeader>

          <LoginForm onSubmit={handleLogin}>
            
            <GoogleButton type="button" onClick={handleGoogleSignIn}>
              <span>
                <img 
                  src="/assets/images/login/guclogo.png" 
                  alt="GUC Logo" 
                  width="20px" 
                  height="20px" 
                />
              </span>
              Sign in with GUC
            </GoogleButton>
            <InputLabel htmlFor="email">Email Address</InputLabel>
            <InputField
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="i.e. davon@mail.com"
            />
            <InputLabel htmlFor="password">Password</InputLabel>
            <InputField
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="********"
            />
            <RememberMeContainer>
              <Checkbox
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              />
              <label htmlFor="rememberMe">Remember me</label>
            </RememberMeContainer>
            <StyledLoginButton type="submit">Login</StyledLoginButton>
            <AccountText>
              Company looking to hire?
              <Link to="/company-register">Register as a company</Link>
            </AccountText>
          </LoginForm>
          
        </FormContainer>
        
      </FormSection>
      
           
      
      
    </LoginContainer>
  );
};

export default Login;